import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import { useToast } from '../../hooks/use-toast';
import MediaPicker from '../../components/MediaPicker';
import { resolveMediaUrl } from '../../utils/mediaUrl';

const AdminCategoryForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEdit = Boolean(id);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [loading, setLoading] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [enabledCountries, setEnabledCountries] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [expandedAccordions, setExpandedAccordions] = useState([]);
  
  const [formData, setFormData] = useState({
    slug: '',
    image: '',
    parentId: null,
    order: 0,
    isActive: true,
    showOnHome: false,
    versions: []
  });

  const getFlagEmoji = (countryCode) => {
    if (!countryCode) return '🌍';
    const codePoints = countryCode.toUpperCase().split('').map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  const generateSlug = (text) => {
    return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  useEffect(() => {
    fetchEnabledCountries();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (isEdit && enabledCountries.length > 0) {
      fetchCategory();
    } else if (!isEdit && enabledCountries.length > 0) {
      initializeFormForCreate();
    }
  }, [id, enabledCountries]);

  const fetchEnabledCountries = async () => {
    try {
      const response = await fetch(`${API_URL}/api/localization/countries`);
      const data = await response.json();
      setEnabledCountries(data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/categories/admin/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setAllCategories(data.filter(cat => (cat.id || cat._id) !== id)); // Exclude current category
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const initializeFormForCreate = () => {
    const versions = enabledCountries.map(country => ({
      countryCode: country.code,
      language: country.language,
      name: '',
      description: '',
      isAvailable: true
    }));

    setFormData({
      slug: '',
      image: '',
      parentId: null,
      order: 0,
      isActive: true,
      showOnHome: false,
      versions: versions
    });
  };

  const fetchCategory = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/categories/admin/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      const category = data.find(c => (c._id || c.id) === id);
      
      if (category) {
        // Merge existing versions with newly enabled countries
        const existingVersions = category.versions || [];
        const mergedVersions = enabledCountries.map(country => {
          // Check if version exists for this country
          const existingVersion = existingVersions.find(v => v.countryCode === country.code);
          
          if (existingVersion) {
            // Return existing version
            return existingVersion;
          } else {
            // Create new empty version for newly enabled country
            return {
              countryCode: country.code,
              language: country.language,
              name: '',
              description: '',
              isAvailable: true
            };
          }
        });
        
        setFormData({
          slug: category.slug || '',
          image: category.image || '',
          parentId: category.parentId || null,
          order: category.order || 0,
          isActive: category.isActive !== undefined ? category.isActive : true,
          showOnHome: category.showOnHome || false,
          versions: mergedVersions
        });
      } else {
        toast({
          title: 'Error',
          description: 'Category not found',
          variant: 'destructive'
        });
        navigate('/admin/categories');
      }
    } catch (error) {
      console.error('Error fetching category:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch category',
        variant: 'destructive'
      });
      navigate('/admin/categories');
    }
  };

  const updateVersionField = (versionIndex, field, value) => {
    const newVersions = [...formData.versions];
    newVersions[versionIndex] = {
      ...newVersions[versionIndex],
      [field]: value
    };
    setFormData({ ...formData, versions: newVersions });
  };

  const handleSave = async () => {
    try {
      if (!formData.slug) {
        toast({
          title: 'Validation Error',
          description: 'Slug is required',
          variant: 'destructive',
        });
        return;
      }

      const hasValidVersion = formData.versions.some(v => v.name && v.name.trim() !== '');
      if (!hasValidVersion) {
        toast({
          title: 'Validation Error',
          description: 'At least one country version with a name is required',
          variant: 'destructive',
        });
        return;
      }

      setLoading(true);
      const token = localStorage.getItem('accessToken');
      let response;

      if (isEdit) {
        response = await fetch(`${API_URL}/api/categories/${id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
      } else {
        response = await fetch(`${API_URL}/api/categories`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save category');
      }

      toast({
        title: 'Success',
        description: isEdit ? 'Category updated successfully' : 'Category created successfully',
      });

      navigate('/admin/categories');
    } catch (error) {
      console.error('Error saving category:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save category',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (imageUrl) => {
    setFormData({ ...formData, image: imageUrl });
    setShowMediaPicker(false);
  };

  const getCategoryDisplayName = (category) => {
    return category.versions?.[0]?.name || category.slug || 'Unnamed Category';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate('/admin/categories')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Edit Category' : 'Add Category'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isEdit ? 'Update category details and country versions' : 'Create a new category with country-specific versions'}
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        {/* Master Category Information */}
        <div className="border-b pb-6">
          <h3 className="text-lg font-semibold mb-4">Master Category Information</h3>
          
          <div className="space-y-4">
            {/* Slug */}
            <div>
              <Label>Slug (URL) *</Label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="category-name"
              />
            </div>

            {/* Parent Category */}
            <div>
              <Label>Parent Category (Optional)</Label>
              <Select
                value={formData.parentId || 'none'}
                onValueChange={(value) => setFormData({ ...formData, parentId: value === 'none' ? null : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="No parent (top-level)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Parent (Top-level)</SelectItem>
                  {allCategories.map((cat) => (
                    <SelectItem key={cat.id || cat._id} value={cat.id || cat._id}>
                      {getCategoryDisplayName(cat)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Image */}
            <div>
              <Label>Category Image</Label>
              <div className="mt-2 space-y-2">
                {formData.image && (
                  <div className="relative inline-block">
                    <img
                      src={resolveMediaUrl(formData.image)}
                      alt="Category"
                      className="w-32 h-32 object-cover rounded border"
                      onError={(e) => { e.currentTarget.src = 'https://placehold.co/128x128?text=No+image'; }}
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-1 right-1 h-6 w-6 p-0"
                      onClick={() => setFormData({ ...formData, image: '' })}
                    >
                      ×
                    </Button>
                  </div>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowMediaPicker(true)}
                  className="w-full"
                >
                  <ImageIcon className="h-4 w-4 mr-2" />
                  {formData.image ? 'Change Image' : 'Select Image'}
                </Button>
              </div>
            </div>

            {/* Order */}
            <div>
              <Label>Display Order</Label>
              <Input
                type="number"
                min="0"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
              />
            </div>

            {/* Checkboxes */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                />
                <Label className="font-normal cursor-pointer">Active Category</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={formData.showOnHome}
                  onCheckedChange={(checked) => setFormData({ ...formData, showOnHome: checked })}
                />
                <Label className="font-normal cursor-pointer">Show on Homepage</Label>
              </div>
            </div>
          </div>
        </div>

        {/* Country Versions Accordion */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Country Versions</h3>
          <p className="text-sm text-gray-600 mb-4">
            Create versions for each country. At least one version with a name is required.
          </p>

          <Accordion type="multiple" value={expandedAccordions} onValueChange={setExpandedAccordions}>
            {formData.versions.map((version, versionIndex) => {
              const country = enabledCountries.find(c => c.code === version.countryCode);
              if (!country) return null;

              return (
                <AccordionItem key={versionIndex} value={`country-${versionIndex}`} className="border rounded-lg mb-2">
                  <AccordionTrigger className="px-4 hover:no-underline">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{getFlagEmoji(country.code)}</span>
                      <div className="text-left">
                        <div className="font-semibold">{country.name}</div>
                        <div className="text-sm text-gray-500">
                          {country.languageName}
                          {version.name && <span className="ml-2 text-blue-600">• {version.name}</span>}
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-4 pt-4">
                      {/* Category Name */}
                      <div>
                        <Label>Category Name ({country.languageName}) *</Label>
                        <Input
                          value={version.name}
                          onChange={(e) => {
                            updateVersionField(versionIndex, 'name', e.target.value);
                            if (!isEdit && versionIndex === 0 && !formData.slug) {
                              setFormData({ ...formData, slug: generateSlug(e.target.value) });
                            }
                          }}
                          placeholder={`Enter category name in ${country.languageName}`}
                        />
                      </div>

                      {/* Description */}
                      <div>
                        <Label>Description ({country.languageName})</Label>
                        <Textarea
                          value={version.description}
                          onChange={(e) => updateVersionField(versionIndex, 'description', e.target.value)}
                          rows={3}
                          placeholder="Category description"
                        />
                      </div>

                      {/* Availability */}
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          checked={version.isAvailable}
                          onCheckedChange={(checked) => updateVersionField(versionIndex, 'isAvailable', checked)}
                        />
                        <Label className="font-normal cursor-pointer">
                          ✓ Available in {country.name}
                        </Label>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>

        {/* Actions */}
        <div className="flex gap-4 justify-end pt-6 border-t">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/categories')}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="bg-[#be1f1a] hover:bg-[#a71b17]"
          >
            {loading ? 'Saving...' : (isEdit ? 'Update Category' : 'Create Category')}
          </Button>
        </div>
      </div>

      {/* Media Picker */}
      <MediaPicker
        isOpen={showMediaPicker}
        onClose={() => setShowMediaPicker(false)}
        onSelect={handleImageSelect}
        fileType="image"
      />
    </div>
  );
};

export default AdminCategoryForm;
